import { passwordSchema } from '@fluxus/shared';
import { useQuery } from '@tanstack/react-query';
import { CircleCheck, MailX } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { AuthScreen } from '@/components/layout/AuthScreen';
import { ErrorState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, PasswordInput, fieldA11y } from '@/components/ui/form';
import { clearHash, PasswordRules, tokenFromHash } from '@/features/auth/password';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';
import { SessionLoadingPage } from './AccessPages';

/** "Aceitar convite" (RF-07): aberta pelo link do e-mail; a pessoa define a senha e a conta é ativada. */
export function AcceptInvitePage() {
  const { client, state } = useAuth();
  const [token] = useState(tokenFromHash);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [linkInvalid, setLinkInvalid] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const ids = { password: useId(), confirm: useId(), alert: useId() };

  const check = useQuery({
    queryKey: ['auth', 'invitation', token],
    queryFn: () => client.checkInvitation(token),
    // Só depois de restaurar a sessão: se ela termina, o cache é limpo e a conferência recomeçaria.
    enabled: token.length > 0 && state.status !== 'loading',
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  });

  // Foco no título no mesmo commit em que a tela troca (leitor de tela anuncia o resultado).
  useLayoutEffect(() => {
    if (done || linkInvalid) headingRef.current?.focus();
  }, [done, linkInvalid]);

  useEffect(clearHash, []);

  // Sucesso primeiro: o convite (agora usado) não pode trocar esta tela pela de convite inválido.
  if (done) {
    return (
      <AuthScreen>
        <span className="inline-flex size-12 items-center justify-center rounded-full bg-status-green-bg text-status-green-fg">
          <CircleCheck className="size-6" aria-hidden />
        </span>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-5 text-3xl font-extrabold tracking-tight outline-none"
        >
          Conta ativada
        </h1>
        <p className="mt-3 text-sm text-muted-foreground" role="status">
          Tudo certo{check.data ? `, ${check.data.name.split(' ')[0]}` : ''}: sua senha foi criada. Entre com
          o e-mail{' '}
          {check.data ? <strong className="text-foreground">{check.data.email}</strong> : 'do convite'} e a
          senha que você acabou de definir.
        </p>
        <Button asChild size="lg" className="mt-8 w-full">
          <Link to="/entrar">Entrar no Fluxus</Link>
        </Button>
      </AuthScreen>
    );
  }

  if (token && check.isPending) return <SessionLoadingPage />;
  if (token && check.isError) {
    return (
      <AuthScreen>
        <ErrorState
          error={check.error}
          title="Não foi possível conferir o convite"
          onRetry={() => void check.refetch()}
          retrying={check.isFetching}
        />
      </AuthScreen>
    );
  }

  const info = check.data;
  if (!token || !info || linkInvalid) {
    return (
      <AuthScreen>
        <span className="inline-flex size-12 items-center justify-center rounded-full bg-status-red-bg text-status-red-fg">
          <MailX className="size-6" aria-hidden />
        </span>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-5 text-3xl font-extrabold tracking-tight outline-none"
        >
          Convite inválido ou expirado
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Cada convite vale por tempo limitado e só pode ser usado uma vez. Se você recebeu mais de um, use o
          mais recente. Se nenhum funcionar, peça um novo convite a quem convidou você.
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          Já ativou sua conta? Entre com seu e-mail e senha.
        </p>
        <Button asChild variant="outline" className="mt-8 w-full">
          <Link to="/entrar">Ir para entrar</Link>
        </Button>
      </AuthScreen>
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setFormError(null);
    const parsed = passwordSchema.safeParse(password);
    const next: typeof errors = {};
    if (!parsed.success) next.password = parsed.error.issues[0]?.message;
    if (!next.password && confirm !== password) next.confirm = 'As senhas não são iguais.';
    setErrors(next);
    if (next.password) return passwordRef.current?.focus();
    if (next.confirm) return confirmRef.current?.focus();
    setSubmitting(true);
    try {
      await client.acceptInvitation(token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INVITATION_INVALID') setLinkInvalid(true);
      else setFormError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthScreen>
      <p className="text-sm font-semibold text-brand-strong dark:text-brand">{info.organizationName}</p>
      <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-balance">
        Boas-vindas, {info.name.split(' ')[0]}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Você foi convidado para usar o Fluxus BSC. Crie sua senha para ativar a conta de{' '}
        <strong className="break-all text-foreground">{info.email}</strong>.
      </p>
      <form
        noValidate
        onSubmit={onSubmit}
        className="mt-8 flex flex-col gap-5"
        aria-describedby={formError ? ids.alert : undefined}
      >
        {/* Ajuda gerenciadores de senha a salvar a senha no e-mail certo. */}
        <input type="email" name="email" autoComplete="username" value={info.email} readOnly hidden />
        <FormField
          id={ids.password}
          label="Crie sua senha"
          error={errors.password}
          hint={<PasswordRules password={password} />}
        >
          <PasswordInput
            ref={passwordRef}
            {...fieldA11y(ids.password, errors.password, true)}
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setErrors((x) => ({ ...x, password: undefined }));
            }}
          />
        </FormField>
        <FormField id={ids.confirm} label="Repita a senha" error={errors.confirm}>
          <PasswordInput
            ref={confirmRef}
            {...fieldA11y(ids.confirm, errors.confirm)}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              setErrors((x) => ({ ...x, confirm: undefined }));
            }}
          />
        </FormField>
        {formError ? (
          <FormAlert id={ids.alert} tone="error">
            {errorMessage(formError)}
          </FormAlert>
        ) : null}
        <Button type="submit" size="lg" className="mt-1 w-full" loading={submitting}>
          Ativar minha conta
        </Button>
      </form>
    </AuthScreen>
  );
}
